/**
 * The query key factory — `FE-04.13`.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS AT ALL, AND WHY IT IS A FILE RATHER THAN A CONVENTION
 * ---------------------------------------------------------------------------
 * TanStack Query caches by a structural comparison of the key array. Two hooks
 * that spell the same cache entry two ways get **two entries**, and the second
 * one is never invalidated by the first one's mutation. Nothing throws, nothing
 * turns red, and the symptom is a board that is stale until reload — the
 * failure mode `FE-04.13` was written to prevent. So no hook builds a key
 * inline. **A block that needs a new entry adds its builder here**, beside the
 * others, where the next block can see the spelling before inventing a second.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THAT MAKES THAT MECHANICAL: THE KEY MIRRORS THE URL'S OWN PATH
 * PARAMETERS, AND CARRIES NOTHING ELSE
 * ---------------------------------------------------------------------------
 *   `GET /boards/:id/contents`  →  ['boards', boardId, 'contents']
 *   `GET /boards/:id/lists`     →  ['boards', boardId, 'lists']
 *   `GET /lists/:id/cards`      →  ['lists', listId, 'cards']
 *   `GET /cards/:id`            →  ['cards', cardId]
 *
 * A parent id appears in a key **iff the route needs it to fetch**. The obvious
 * alternative — nesting everything under its board, `['boards', boardId,
 * 'cards', cardId]`, so that one board-shaped invalidation sweeps the whole
 * subtree — was considered and **rejected**, because it makes the key a
 * function of data the request does not need:
 *
 *   - the board face knows a card's `boardId` (it came from `/contents`), but a
 *     deep link to `/c/:shortLink` resolves a card **before** anything knows
 *     which board it is on;
 *   - so two call sites would key the same card two ways — with a board id and
 *     without one — which is exactly the duplicate-entry bug above, reintroduced
 *     by the mechanism meant to avoid it.
 *
 * Mirroring the path parameters removes the judgement call: there is only one
 * spelling because there is only one URL.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE COST OF THAT CHOICE, RECORDED RATHER THAN GLOSSED
 * ---------------------------------------------------------------------------
 * A live frame (`liveFrameSchema` in `@molt/shared`, which is `eventSchema`
 * aliased) carries **`boardId` and `cardId` and nothing else** — there is no
 * `listId` on the wire. So a board event can reach `['boards', boardId, …]` and
 * `['cards', cardId]` by prefix, and **cannot** reach `['lists', listId,
 * 'cards']`, because it does not know the list. `eventBlastRadius` below is
 * that pair, named once, and this paragraph is the known limit `FE-18.1`
 * inherits rather than discovers. It is not a reason to re-nest: `FE-04.16`'s
 * own text says it delivers deltas **into** the cache (`setQueryData` against a
 * known entry), so a prefix sweep is its fallback, not its mechanism.
 *
 * ---------------------------------------------------------------------------
 * NO GENERIC ESCAPE HATCH, DELIBERATELY
 * ---------------------------------------------------------------------------
 * There is no `key(...parts)` helper. One would let a block spell an entry
 * without adding it here, which is the two-spellings problem with an import
 * statement in front of it. Add the builder; the diff is two lines.
 *
 * This module imports nothing from `./api`, and that direction is load-bearing:
 * `FE-04.16`'s socket client needs keys and must not drag `fetch` in with them.
 *
 * The one import below is a **type**, erased at compile time, so that direction
 * still holds.
 */

import type { ArchiveType, AutomationCommandKind, BoardListState } from '@molt/shared';

export type BoardArchiveListState = 'open' | 'archived';

/**
 * What every builder below returns, and what TanStack Query accepts.
 *
 * Widened here so a consumer can hold a key without restating one builder's
 * exact tuple type; the builders themselves return precise `as const` tuples,
 * so `queryKeys.cards.detail(id)[0]` is still the literal `'cards'`.
 */
export type MoltQueryKey = readonly unknown[];

/**
 * The factory.
 *
 * First intended callers, per entry: `boards.contents` is `FE-13.x`'s board
 * face, `lists.cards` is `FE-11.x`'s list strip, `cards.detail` is `FE-14.x`'s
 * card back, `me` is `FE-06.1`'s session probe, and `health` is already read by
 * `apps/web/src/routes/index.tsx`.
 *
 * Grouped by the URL's first segment rather than by feature, because the URL is
 * the one naming both halves of this product already agree on.
 */
export const queryKeys = {
  /**
   * `GET /health`. Spelled `['health']` because
   * `apps/web/src/routes/index.tsx` already spells it that way — the factory
   * adopts the existing entry rather than orphaning it, which is the whole
   * point of the file.
   */
  health: () => ['health'] as const,

  /**
   * `GET /me` — the signed-in account. `FE-06.1` is the first caller: it is the
   * query whose `unauthenticated` failure tells the router there is no session.
   */
  me: () => ['me'] as const,

  /** `GET /me/inbox/cards` — B43/B74 private Inbox cards, varied by querystring. */
  meInboxCards: (queryKey = '') =>
    queryKey === ''
      ? (['me', 'inbox', 'cards'] as const)
      : (['me', 'inbox', 'cards', queryKey] as const),

  /** `GET /me/inbox/cards/:id` — one private Inbox card. */
  meInboxCard: (cardId: string) => ['me', 'inbox', 'cards', cardId] as const,

  /** `GET /me/inbox/email-address` — B74 private Inbox capture address. */
  meInboxEmailAddress: () => ['me', 'inbox', 'email-address'] as const,

  /** `GET /me/inbox/background` — B74 private Inbox background preference. */
  meInboxBackground: () => ['me', 'inbox', 'background'] as const,

  /** `GET /me/cards` — B43 My Cards, varied by querystring. */
  meCards: (queryKey = '') =>
    queryKey === '' ? (['me', 'cards'] as const) : (['me', 'cards', queryKey] as const),

  /** `GET /me/home` — B76 private Home aggregate, varied by querystring. */
  meHome: (queryKey = '') =>
    queryKey === '' ? (['me', 'home'] as const) : (['me', 'home', queryKey] as const),

  /** `GET /me/appearance-preferences` — B42 private appearance preferences. */
  meAppearance: () => ['me', 'appearance-preferences'] as const,

  /** `GET /me/accessibility-preferences` — B41 private accessibility preferences. */
  meAccessibility: () => ['me', 'accessibility-preferences'] as const,

  /** `GET /me/locale-preferences` — B44 private locale/time preferences. */
  meLocale: () => ['me', 'locale-preferences'] as const,

  /**
   * `GET /me/card-back-preferences` — `FR-CARD-034`'s activity-section collapse
   * (`FE-16.1`, over `BE-14.5`'s routes).
   *
   * ⚠ Under `me`, not under `cards`, for `meDisplayPreferences`' reason stated
   * against a second column: `user_preferences.activity_section_collapsed` has
   * no `card_id`, so one cache entry serves every card back the member opens —
   * which IS the requirement ("the preference persists per user **across
   * cards**"). A card-scoped key here would invent a per-card answer the server
   * cannot give, and would break AC2 by construction.
   *
   * ⚠ `eventBlastRadius` does not reach it, and should not: the sweep emits
   * `['boards', …]` and `['cards', …]` prefixes, and this is private per-viewer
   * state no other account can change, so there is no live frame it belongs to.
   */
  meCardBackPreferences: () => ['me', 'card-back-preferences'] as const,

  /** `GET /me/message-catalogue` — B80 private interface-language catalogue. */
  meMessageCatalogue: () => ['me', 'message-catalogue'] as const,

  /** `POST /me/onboarding/welcome-board` — B46 first-run provisioning. */
  meOnboardingWelcomeBoard: () => ['me', 'onboarding', 'welcome-board'] as const,

  /**
   * `GET /me/display-preferences` — `FR-VIEW-011` focus mode.
   *
   * ⚠ Under `me`, not under `boards`, and that is the contract rather than a
   * filing choice: `user_preferences.focus_mode_enabled` has no `board_id`, so
   * one cache entry serves every board the member opens. A board-scoped key
   * here would invent a per-board answer the server cannot give.
   */
  meDisplayPreferences: () => ['me', 'display-preferences'] as const,

  /** `GET /me/onboarding/tips` — `FR-EXP-033` AC2 one-time tip state. */
  meOnboardingTips: () => ['me', 'onboarding', 'tips'] as const,

  /** `GET /me/connected-apps` — B49 private connected-tool grants. */
  meConnectedApps: () => ['me', 'connected-apps'] as const,

  /** `POST /me/offline-sync` — B81 queued offline-operation replay. */
  meOfflineSync: () => ['me', 'offline-sync'] as const,

  /** `GET /me/planner` — B75 private Planner surface, varied by visible range. */
  mePlanner: (queryKey = '') =>
    queryKey === ''
      ? (['me', 'planner'] as const)
      : (['me', 'planner', queryKey] as const),

  /** `GET /me/planner/calendar-accounts` — B75 private Planner calendar accounts. */
  mePlannerCalendarAccounts: () => ['me', 'planner', 'calendar-accounts'] as const,

  /** `GET /me/first-party-integrations` — B73 private provider account states. */
  meFirstPartyIntegrations: () => ['me', 'first-party-integrations'] as const,

  /** `GET /me/change-registrations` — B73 outbound webhook registrations. */
  meChangeRegistrations: () => ['me', 'change-registrations'] as const,

  boards: {
    /**
     * Every board-shaped entry. The prefix `FE-09.x` invalidates after creating
     * or archiving a board, and the coarsest sweep in the file.
     */
    all: () => ['boards'] as const,
    /** `GET /boards/:id`. */
    detail: (boardId: string) => ['boards', boardId] as const,
    /** Every `GET /boards?state=` page entry. */
    pages: () => ['boards', 'page'] as const,
    /**
     * `GET /boards/:id/contents` — the composite the whole board face binds to
     * (`boardContentsSchema`). `FE-13.x` is the first caller.
     */
    contents: (boardId: string, filterKey = '') =>
      filterKey === ''
        ? (['boards', boardId, 'contents'] as const)
        : (['boards', boardId, 'contents', filterKey] as const),
    /** `GET /boards/:id/calendar` — B51 board Calendar read, varied by range/filter. */
    calendar: (boardId: string, queryKey = '') =>
      queryKey === ''
        ? (['boards', boardId, 'calendar'] as const)
        : (['boards', boardId, 'calendar', queryKey] as const),
    /** `GET /boards/:id/timeline` — B52 board Timeline read, varied by range/filter/grouping. */
    timeline: (boardId: string, queryKey = '') =>
      queryKey === ''
        ? (['boards', boardId, 'timeline'] as const)
        : (['boards', boardId, 'timeline', queryKey] as const),
    /** `GET /boards/:id/dashboard` — B53 board Dashboard read, varied by board filter. */
    dashboard: (boardId: string, queryKey = '') =>
      queryKey === ''
        ? (['boards', boardId, 'dashboard'] as const)
        : (['boards', boardId, 'dashboard', queryKey] as const),
    /** `GET /boards/:id/map` — B54 board Map read, varied by board filter. */
    map: (boardId: string, queryKey = '') =>
      queryKey === ''
        ? (['boards', boardId, 'map'] as const)
        : (['boards', boardId, 'map', queryKey] as const),
    /** `GET /boards/:id/table` — B50 board Table read, varied by board filter. */
    table: (boardId: string, queryKey = '') =>
      queryKey === ''
        ? (['boards', boardId, 'table'] as const)
        : (['boards', boardId, 'table', queryKey] as const),
    /** `GET /boards/:id/lists?state=` — the list strip and destination pickers. */
    lists: (boardId: string, state: BoardArchiveListState = 'open') =>
      ['boards', boardId, 'lists', state] as const,
    /**
     * `GET /boards/:id/members` — `FE-08.2`'s only use for it is learning the
     * **caller's own** role, because nothing on `boardSchema` carries it and no
     * "my capabilities" route exists (`D3-BRIEF.md` §3.3). `FE-10.2` (chunk
     * `D4`) is the caller that will render the list itself.
     *
     * Under the `['boards', boardId, …]` prefix on purpose, so a live socket
     * frame sweeps it with the rest of the board — unlike `page` below.
     */
    members: (boardId: string) => ['boards', boardId, 'members'] as const,
    /** `GET /boards/:id/silence` — B77 private per-user board mute state. */
    silence: (boardId: string) => ['boards', boardId, 'silence'] as const,
    /**
     * `GET /boards/:id/member-permission` — `FR-PERM-027`'s stored value.
     *
     * `FE-10.2` (chunk `D4`) reads it for two reasons and writes it for
     * neither: `FR-PERM-018` AC4 ("given a board member without invite rights,
     * then no add-member control is offered") cannot be answered without it,
     * and the same response fills the *Who can add and remove members* row of
     * the settings surface. The **control** that writes it is `FE-38.5`'s.
     *
     * Under the `['boards', boardId, …]` prefix, like `members` and for the
     * same reason — a live socket frame sweeps it with the rest of the board.
     */
    memberPermission: (boardId: string) =>
      ['boards', boardId, 'member-permission'] as const,
    /** `GET /boards/:id/invite-link` — B38 board invite-link credential facet. */
    inviteLink: (boardId: string) => ['boards', boardId, 'invite-link'] as const,
    /** `GET /boards/:id/email-to-board` — B49 private per-member inbound address. */
    emailToBoard: (boardId: string) => ['boards', boardId, 'email-to-board'] as const,
    /** `GET /boards/:id/first-party-integrations` — B73 board-scoped provider states. */
    firstPartyIntegrations: (boardId: string) =>
      ['boards', boardId, 'first-party-integrations'] as const,
    /** `GET /boards/:id/calendar-feed` — B49 private per-member calendar feed. */
    calendarFeed: (boardId: string) => ['boards', boardId, 'calendar-feed'] as const,
    /** `GET /boards/:id/snapshots` — B79 workspace-admin board snapshots. */
    snapshots: (boardId: string) => ['boards', boardId, 'snapshots'] as const,
    /** `GET /boards/:id/commenting-permission` — B16/B30, surfaced by FE-38.5. */
    commentingPermission: (boardId: string) =>
      ['boards', boardId, 'commenting-permission'] as const,
    /** `GET /boards/:id/voting-enabled` — B59 board voting setting facet. */
    votingEnabled: (boardId: string) => ['boards', boardId, 'voting-enabled'] as const,
    /** `GET /boards/:id/voting-permission` — B59 board voting setting facet. */
    votingPermission: (boardId: string) =>
      ['boards', boardId, 'voting-permission'] as const,
    /** `GET /boards/:id/voting-hide-tallies` — B59 board voting setting facet. */
    votingHideTallies: (boardId: string) =>
      ['boards', boardId, 'voting-hide-tallies'] as const,
    /** `GET /boards/:id/card-aging-mode` — B60 board card-aging setting facet. */
    cardAgingMode: (boardId: string) => ['boards', boardId, 'card-aging-mode'] as const,
    /** `GET /boards/:id/self-join` — B38 workspace-member join/edit toggle. */
    selfJoin: (boardId: string) => ['boards', boardId, 'self-join'] as const,
    /** `GET /boards/:id/star` — B48 private per-member board star state. */
    star: (boardId: string) => ['boards', boardId, 'star'] as const,
    /** `GET /boards/:id/view` — B40 private per-member last-view state. */
    view: (boardId: string) => ['boards', boardId, 'view'] as const,
    /** `GET /boards/:id/collapsed-lists` — B40 private per-member list collapse state. */
    collapsedLists: (boardId: string) => ['boards', boardId, 'collapsed-lists'] as const,
    /** `GET /boards/:id/pinned-views` — private per-member pinned header views (`FE-55.1`). */
    pinnedViews: (boardId: string) => ['boards', boardId, 'pinned-views'] as const,
    /** `GET /boards/:id/background` — B42 shared board background. */
    background: (boardId: string) => ['boards', boardId, 'background'] as const,
    /** `GET /boards/:id/background/custom` — B58 reusable custom backgrounds. */
    customBackgrounds: (boardId: string) =>
      ['boards', boardId, 'background', 'custom'] as const,
    /** `GET /boards/:id/capacity` — B47 board object-limit counters. */
    capacity: (boardId: string) => ['boards', boardId, 'capacity'] as const,
    /** `GET /boards/:id/template` — B36 board template facet. */
    template: (boardId: string) => ['boards', boardId, 'template'] as const,
    /** `GET /boards/:id/card-templates` — B37 same-board template-card picker. */
    cardTemplates: (boardId: string) => ['boards', boardId, 'card-templates'] as const,
    /** `GET /boards/:id/labels` — the board label set (`FE-15.1`). */
    labels: (boardId: string) => ['boards', boardId, 'labels'] as const,
    /** `GET /boards/:id/custom-fields` — B56 board custom-field definitions. */
    customFields: (boardId: string) => ['boards', boardId, 'custom-fields'] as const,
    /** `GET /boards/:id/stickers` — B58 sticker library. */
    stickers: (boardId: string) => ['boards', boardId, 'stickers'] as const,
    /** `GET /boards/:id/archive` — the archived items recovery surface (`FE-22.2`). */
    archiveAll: (boardId: string) => ['boards', boardId, 'archive'] as const,
    archive: (
      boardId: string,
      type: ArchiveType,
      q?: string,
      cursor?: string,
      limit?: number,
    ) =>
      ['boards', boardId, 'archive', type, q ?? '', cursor ?? '', limit ?? ''] as const,
    /** `GET /boards/:id/activity?filter=` — board menu activity feed (`FE-28.2`). */
    activity: (boardId: string, filter: 'all' | 'comments') =>
      ['boards', boardId, 'activity', filter] as const,
    /** `GET /boards/:id/automations?commandKind=` — B68 board Automation surface. */
    automations: (boardId: string, commandKind?: AutomationCommandKind) =>
      commandKind === undefined
        ? (['boards', boardId, 'automations'] as const)
        : (['boards', boardId, 'automations', commandKind] as const),
    /** `GET /boards/:id/automation-buttons` — B69 board-header runtime buttons. */
    automationButtons: (boardId: string) =>
      ['boards', boardId, 'automation-buttons'] as const,
    /**
     * `GET /boards/by-short-link/:shortLink` — `FR-EXP-015`. The resolver every
     * board URL goes through, since `/b/<shortLink>/<slug>` is what a member
     * holds and `:id` is what `contents` needs. `FE-13.1` is the first caller;
     * `FE-08.4` is the one that will care about the `path` on its response.
     *
     * ⚠ **`'by-short-link'` is a literal segment of the URL and is in the key
     * for that reason**, not as a namespace. Without it the key would be
     * `['boards', shortLink]`, which is `detail`'s spelling for a *different*
     * route — two routes, one cache entry, and the second one to write wins.
     * The two id spaces cannot collide today (a ULID is 26 Crockford
     * characters, a short link is eight), so the bug would not be a wrong
     * board; it would be a board record that is sometimes `boardSchema` and
     * sometimes `boardAddressSchema`, depending on which route filled it last.
     *
     * ⚠ **`eventBlastRadius` does NOT sweep this entry, and the sentence that
     * used to stand here saying it did was false.** Measured in `D3` by agent
     * J and corrected in place, because a wrong claim in the file that owns the
     * behaviour is worse than no claim. The sweep emits
     * `['boards', boardId]`, and prefix matching asks whether that is a
     * *prefix* of the entry's key — `['boards', 'by-short-link', x]` begins
     * `['boards', 'by-short-link']`, so it matches only if a board's id were
     * literally the string `'by-short-link'`. Being "under the `['boards', …]`
     * prefix" is not the test; being under the *emitted* prefix is.
     *
     * What that costs today, exactly: a board renamed or closed **by somebody
     * else** updates `contents` — which IS `['boards', boardId, 'contents']`
     * and IS swept — while this entry keeps the old `title`, `slug`, `path`
     * and `closed`. `D3`'s own mutations are unaffected: `lib/boards.ts`
     * invalidates both entries explicitly rather than relying on the sweep.
     * **Widening the sweep belongs to `FE-18.1` (chunk `H1`), which owns live
     * board updates** — it is not `D3`'s to change, and `D3` records it rather
     * than quietly fixing a socket it does not own.
     */
    byShortLink: (shortLink: string) => ['boards', 'by-short-link', shortLink] as const,
    /**
     * `GET /boards?state=open|closed` — the boards landing page's composite
     * (`boardsPageSchema`). `FE-23.1` (chunk `D2`) is the first caller.
     *
     * ⚠ **`'page'` is a discriminating literal, not a URL segment**, and it is
     * the one place in this file the key does not mirror a path. This route has
     * no path parameters at all — it varies by *querystring* — so the mirroring
     * rule has nothing to bite on, and the naive `['boards', state]` would be
     * `detail`'s spelling for a different route with `'open'` in the id
     * position. The literal keeps the two apart, for the same reason
     * `by-short-link` does.
     *
     * ⚠ **`eventBlastRadius` does NOT sweep this entry either**, and the
     * sentence that used to claim it did was false for the same reason — see
     * `byShortLink` above for the mechanics. This one matters more, because the
     * landing page is where a board that somebody else closed should stop
     * appearing. Every mutation in `lib/boards.ts` therefore invalidates the
     * `['boards', 'page']` prefix **itself**, and `D3-BRIEF.md` §5.6 says so;
     * a chunk that adds a board mutation and relies on the socket will find
     * the landing page stale with nothing red.
     */
    page: (state: BoardListState) => ['boards', 'page', state] as const,
  },

  /**
   * Workspaces — `FE-07.x` (chunk `D2`). Two entries, both mirroring their
   * route's path exactly: `GET /workspaces` and `GET /workspaces/:id/members`.
   *
   * There is deliberately no `detail(workspaceId)`. `GET /workspaces/:id`
   * exists, but nothing in `apps/web` calls it — the member surface reads the
   * name it needs out of the `all()` list rather than issuing a second request
   * for a heading (`D2-BRIEF.md` §6.8 rule 4). `B39` is the block that will
   * want it; adding the builder before it has a caller would be the untested
   * unused export `getUser`'s docstring argues against.
   */
  workspaces: {
    /** `GET /workspaces` — every workspace the caller is a member of. */
    all: () => ['workspaces'] as const,
    /** `GET /workspaces/:id` — the workspace settings host. */
    detail: (workspaceId: string) => ['workspaces', workspaceId] as const,
    /** `GET /workspaces/:id/boards?state=` — destination board pickers. */
    boards: (workspaceId: string, state: BoardListState = 'open') =>
      ['workspaces', workspaceId, 'boards', state] as const,
    /** `GET /workspaces/:id/calendar` — B51 Workspace Calendar, varied by querystring. */
    calendar: (workspaceId: string, queryKey = '') =>
      queryKey === ''
        ? (['workspaces', workspaceId, 'calendar'] as const)
        : (['workspaces', workspaceId, 'calendar', queryKey] as const),
    /** `GET /workspaces/:id/table` — B50 Workspace Table, varied by selected boards and filters. */
    table: (workspaceId: string, queryKey = '') =>
      queryKey === ''
        ? (['workspaces', workspaceId, 'table'] as const)
        : (['workspaces', workspaceId, 'table', queryKey] as const),
    /** `GET /workspaces/:id/activity` — B66 Workspace activity feed. */
    activity: (
      workspaceId: string,
      filter: 'all' | 'comments',
      memberId?: string | null,
    ) =>
      memberId === null || memberId === undefined
        ? (['workspaces', workspaceId, 'activity', filter] as const)
        : (['workspaces', workspaceId, 'activity', filter, memberId] as const),
    /** `GET /workspaces/:id/automation-activity` — B70 workspace automation runs. */
    automationActivity: (
      workspaceId: string,
      boardId?: string | null,
      outcome?: 'succeeded' | 'warning' | 'failed' | null,
      limit = 25,
    ) =>
      [
        'workspaces',
        workspaceId,
        'automation-activity',
        boardId ?? '',
        outcome ?? '',
        limit,
      ] as const,
    /** `GET /workspaces/:id/automation-usage` — B70 current-period counters. */
    automationUsage: (workspaceId: string) =>
      ['workspaces', workspaceId, 'automation-usage'] as const,
    /** `GET /workspaces/:id/automation-admin` — B70 workspace-admin review. */
    automationAdmin: (workspaceId: string, boardId?: string | null) =>
      ['workspaces', workspaceId, 'automation-admin', boardId ?? ''] as const,
    /** `GET /workspaces/:id/views` — B55 saved Workspace views list. */
    views: (workspaceId: string) => ['workspaces', workspaceId, 'views'] as const,
    /** `GET /workspaces/:id/members`. */
    members: (workspaceId: string) => ['workspaces', workspaceId, 'members'] as const,
    /** `GET /workspaces/:id/join-requests` — B38 admin queue. */
    joinRequests: (workspaceId: string) =>
      ['workspaces', workspaceId, 'join-requests'] as const,
    /** `GET /workspaces/:id/board-visibility-policies` — B67 workspace policy matrix. */
    boardVisibilityPolicies: (workspaceId: string) =>
      ['workspaces', workspaceId, 'board-visibility-policies'] as const,
    /** `GET /workspaces/:id/guest-invite-rule` — B67 workspace guest invite rule. */
    guestInviteRule: (workspaceId: string) =>
      ['workspaces', workspaceId, 'guest-invite-rule'] as const,
    /** `GET /workspaces/:id/guests` — B67 derived workspace guest list. */
    guests: (workspaceId: string) => ['workspaces', workspaceId, 'guests'] as const,
    /** `GET /workspaces/:id/enterprise` — B67 workspace-to-Enterprise linkage. */
    enterprise: (workspaceId: string) =>
      ['workspaces', workspaceId, 'enterprise'] as const,
  },

  enterprises: {
    /** Every enterprise entry — the member-initials sweep marks them stale. */
    all: () => ['enterprises'] as const,
    /** `GET /enterprises/:id/policy` — B67 Enterprise administration policy. */
    policy: (enterpriseId: string) => ['enterprises', enterpriseId, 'policy'] as const,
    /** `GET /enterprises/:id/members` — B67 Enterprise managed-member list. */
    members: (enterpriseId: string) => ['enterprises', enterpriseId, 'members'] as const,
  },

  templates: {
    /** Every public-template gallery read. */
    all: () => ['templates'] as const,
    /** `GET /templates?category=&q=` — B65 public template gallery. */
    gallery: (queryKey = '') =>
      queryKey === '' ? (['templates'] as const) : (['templates', queryKey] as const),
  },

  powerUps: {
    /** Every Power-Up catalogue entry. */
    all: () => ['power-ups'] as const,
    /** `GET /power-ups?…` — B72 directory browse/search. */
    directory: (queryKey = '') =>
      queryKey === ''
        ? (['power-ups', 'directory'] as const)
        : (['power-ups', 'directory', queryKey] as const),
    /** `GET /power-ups/:id` — B72 directory detail. */
    detail: (powerUpId: string) => ['power-ups', powerUpId] as const,
    /** `GET /boards/:id/power-ups` — enabled board Power-Ups. */
    board: (boardId: string) => ['power-ups', 'board', boardId] as const,
    /** `GET /boards/:id/power-ups/surfaces` — enabled Power-Up runtime manifest. */
    boardSurfaces: (boardId: string) =>
      ['power-ups', 'board', boardId, 'surfaces'] as const,
    /** `GET /enterprises/:id/power-ups/policy` — Enterprise Power-Up policy. */
    enterprisePolicy: (enterpriseId: string) =>
      ['power-ups', 'enterprise', enterpriseId, 'policy'] as const,
    /** `GET /enterprises/:id/power-ups/usage` — Enterprise Power-Up usage. */
    enterpriseUsage: (enterpriseId: string) =>
      ['power-ups', 'enterprise', enterpriseId, 'usage'] as const,
    /** Every `GET /enterprises/:id/power-ups/:powerUpId/affected-boards`. */
    enterpriseAffectedBoardsAll: (enterpriseId: string) =>
      ['power-ups', 'enterprise', enterpriseId, 'affected-boards'] as const,
    /** `GET /enterprises/:id/power-ups/:powerUpId/affected-boards`. */
    enterpriseAffectedBoards: (enterpriseId: string, powerUpId: string) =>
      ['power-ups', 'enterprise', enterpriseId, 'affected-boards', powerUpId] as const,
  },

  workspaceViews: {
    /** Every saved Workspace view entry. */
    all: () => ['workspace-views'] as const,
    /** `GET /workspace-views/:id` — B55 saved Workspace view configuration. */
    detail: (viewId: string) => ['workspace-views', viewId] as const,
    /** `GET /workspace-views/:id/table` — B55 saved Workspace Table render. */
    table: (viewId: string) => ['workspace-views', viewId, 'table'] as const,
    /** `GET /workspace-views/:id/calendar?from=&to=` — B55 saved Workspace Calendar render. */
    calendar: (viewId: string, queryKey = '') =>
      queryKey === ''
        ? (['workspace-views', viewId, 'calendar'] as const)
        : (['workspace-views', viewId, 'calendar', queryKey] as const),
  },

  lists: {
    /** Every list-shaped entry. */
    all: () => ['lists'] as const,
    /** `GET /lists/:id`. */
    detail: (listId: string) => ['lists', listId] as const,
    /** `GET /lists/:id/move-preview` — FE-21.2 pre-confirm reconciliation. */
    movePreview: (listId: string, boardId: string) =>
      ['lists', listId, 'move-preview', boardId] as const,
    /**
     * `GET /lists/:id/cards`. Note this is keyed by the **list**, not the
     * board — see the limit recorded above: no live frame names a list, so
     * this entry is unreachable from an event and must be refreshed through
     * its board's `contents`.
     */
    cards: (listId: string) => ['lists', listId, 'cards'] as const,
  },

  cards: {
    /** Every card-shaped entry. */
    all: () => ['cards'] as const,
    /** `GET /cards/:id` — the card back. */
    detail: (cardId: string) => ['cards', cardId] as const,
    /** `GET /card-mirrors/:id` — B64 destination-board mirror surface. */
    mirror: (mirrorId: string) => ['card-mirrors', mirrorId] as const,
    /** `GET /cards/:id/template` — B37 card template facet. */
    template: (cardId: string) => ['cards', cardId, 'template'] as const,
    /** `GET /cards/:id/field-versions` — B31 stale-write header source. */
    fieldVersions: (cardId: string) => ['cards', cardId, 'field-versions'] as const,
    /**
     * `GET`/`PUT`/`DELETE /cards/:id/description/draft` — this viewer's own
     * unsaved description text (`FE-14.2`, `FR-CARD-003` AC2), added by the
     * Card-Back State contract chunk.
     *
     * ⚠ **Private per-viewer state under a shared card's prefix.** The entry is
     * one account's half-typed sentence, but it is keyed by the card because
     * the route is, and `'description-draft'` is a discriminating literal
     * rather than a path segment (the URL spells it `description/draft`) — one
     * segment, so it cannot collide with a card id.
     *
     * ⚠ **`eventBlastRadius` reaches it**, since it sits under
     * `['cards', cardId]`, so any live frame naming the card invalidates and
     * refetches it. That is harmless only because the single consumer
     * (`CardBack.tsx`) reads the data in a click handler and never reconciles
     * on it changing; an effect that seeded the editor from this entry would
     * clobber a person's typing whenever another member touched the card.
     */
    descriptionDraft: (cardId: string) => ['cards', cardId, 'description-draft'] as const,
    /** `GET /cards/:id/move-preview` — FE-21.2 pre-confirm reconciliation. */
    movePreview: (cardId: string, boardId: string, listId: string) =>
      ['cards', cardId, 'move-preview', boardId, listId] as const,
    /** `GET /cards/:id/comments` — the paged card-back comment stream (`FE-16.x`). */
    comments: (cardId: string) => ['cards', cardId, 'comments'] as const,
    /**
     * `GET /cards/:id/comment-count` — the card back's header count
     * (`docs/notes/card-back-header-meta.md`). A SIBLING of `comments`, never a
     * child: every write to `comments` is an exact-key `setQueryData` with a
     * stream updater, and a child key would be one prefix-wide write away from
     * receiving a stream. Under `['cards', cardId]`, so every live frame and every
     * card-detail invalidation refetches it.
     */
    commentCount: (cardId: string) => ['cards', cardId, 'comment-count'] as const,
    /** `GET /cards/:id/activity?filter=` — the card-back merged activity stream (`FE-28.1`). */
    activity: (cardId: string, filter: 'all' | 'comments') =>
      ['cards', cardId, 'activity', filter] as const,
    /** `GET /cards/:id/automation-buttons` — B69 card-back runtime buttons. */
    automationButtons: (cardId: string) =>
      ['cards', cardId, 'automation-buttons'] as const,
    /** `GET /cards/:id/attachments` — the card-back attachment section (`FE-27.x`). */
    attachments: (cardId: string) => ['cards', cardId, 'attachments'] as const,
    /** `GET /cards/:id/relations` — the related-card section (`FE-27.4`). */
    relations: (cardId: string) => ['cards', cardId, 'relations'] as const,
    /** `GET /cards/:id/checklists` — the card-back checklist refresh surface (`FE-26.x`). */
    checklists: (cardId: string) => ['cards', cardId, 'checklists'] as const,
    /** `GET /cards/:id/vote` — B59 current caller vote state. */
    vote: (cardId: string) => ['cards', cardId, 'vote'] as const,
    /** `GET /cards/:id/votes` — B59 card-back voter tally. */
    votes: (cardId: string) => ['cards', cardId, 'votes'] as const,
    /** `GET /cards/:id/email-to-card` — B73 private per-member inbound card address. */
    emailToCard: (cardId: string) => ['cards', cardId, 'email-to-card'] as const,
    /** `GET /cards/:id/export` — B63 deterministic single-card JSON export. */
    export: (cardId: string) => ['cards', cardId, 'export'] as const,
    /** `GET /cards/:id/embed` — B63 read-only external card representation. */
    embed: (cardId: string) => ['cards', cardId, 'embed'] as const,
    /**
     * `GET /cards/by-short-link/:shortLink` — `FR-EXP-015`, `FR-CARD-005`.
     * Added by chunk `D3` (`FE-08.4`, agent J); its first caller is
     * `routes/c.$shortLink.{-$slug}.tsx`, and `FE-14.1`'s card back (chunk
     * `E1`) is the second.
     *
     * ⚠ **`'by-short-link'` is a literal segment of the URL and is in the key
     * for that reason**, exactly as `boards.byShortLink` above. Without it the
     * key would be `['cards', shortLink]`, which is `detail`'s spelling for a
     * *different* route — and the two routes answer different shapes
     * (`cardAddressSchema` is an address, `cardSchema` is a card), so the entry
     * would be one or the other depending on which route filled it last.
     *
     * ⚠ **`eventBlastRadius` does not reach this entry.** A live frame yields
     * the prefix `['cards', cardId]`, and a card id is not `'by-short-link'`,
     * so no partial match exists. That is stated here rather than left to be
     * assumed, because the same is true of `boards.byShortLink` above, whose
     * docstring claims the opposite (reported by agent J; not patched here —
     * it is agent A's prose). An address entry is refreshed by navigating, not
     * over the socket, and nothing in this product mutates a short link.
     */
    byShortLink: (shortLink: string) => ['cards', 'by-short-link', shortLink] as const,
  },

  automations: {
    /** `GET /automations/:id` — B68 command definition read. */
    detail: (commandId: string) => ['automations', commandId] as const,
    /** `GET /automations/:id/log` — B70 per-command run log and diagnostics. */
    log: (commandId: string) => ['automations', commandId, 'log'] as const,
  },

  notifications: {
    /** Every notification-shaped entry. */
    all: () => ['notifications'] as const,
    /** `GET /me/notification-preferences`. */
    preferences: () => ['notifications', 'preferences'] as const,
    /** `GET /me/mobile-push-installations`. */
    mobilePushInstallations: () =>
      ['notifications', 'mobile-push-installations'] as const,
    /** `GET /me/web-push-config` — the VAPID public key, or null. */
    webPushConfig: () => ['notifications', 'web-push-config'] as const,
    /** `GET /notifications`. */
    list: (unread: boolean) => ['notifications', 'list', unread] as const,
    /** `GET /notifications/unread-count`. */
    unreadCount: () => ['notifications', 'unread-count'] as const,
  },

  watches: {
    /** Every watch-shaped entry. */
    all: () => ['watches'] as const,
    /** `GET /cards|lists|boards/:id/watch` — exact-target watch state. */
    detail: (targetType: 'card' | 'list' | 'board', targetId: string) =>
      ['watches', targetType, targetId] as const,
  },

  search: {
    /** `GET /search?q=` — global typeahead results, varying by querystring. */
    typeahead: (q: string) => ['search', 'typeahead', q] as const,
    /** `GET /search/jump-to` — empty-query starred/recent board shortcuts. */
    jumpTo: () => ['search', 'jump-to'] as const,
    /**
     * `GET /search/results` — full result page rows and lifecycle toggles.
     *
     * ⚠ `matchDescriptions` is the fourth key component and it is not
     * optional to the *cache*: `FR-SEARCH-017`'s description toggle changes
     * which rows come back for an otherwise identical `q`, so without it two
     * searches differing only in their text source share one entry and the
     * second is served the first's rows. It defaults to `true` because that
     * is the wire default (`searchFullResultsPageQuerySchema`,
     * `z.stringbool().default(true)`) — an omitted parameter means
     * descriptions ARE matched — so a three-argument call keeps naming the
     * exact entry it has always named.
     */
    fullResults: (
      q: string,
      includeArchived: boolean,
      includeClosedBoards: boolean,
      matchDescriptions = true,
    ) =>
      [
        'search',
        'results',
        q,
        includeArchived,
        includeClosedBoards,
        matchDescriptions,
      ] as const,
    /** `GET /search/adjacent-products` — B78 linked-provider results. */
    adjacentProducts: (q: string, provider = '') =>
      provider === ''
        ? (['search', 'adjacent-products', q] as const)
        : (['search', 'adjacent-products', q, provider] as const),
  },

  savedSearches: {
    /** `GET /saved-searches` — B78 personal saved-search rows. */
    list: () => ['saved-searches'] as const,
    /** `GET /saved-searches/:id/results` — B78 saved-search run response. */
    results: (id: string) => ['saved-searches', id, 'results'] as const,
  },

  /**
   * User-shaped entries — `FE-06.2`. Distinct from `me()` above: `me()` is
   * the signed-in account's own view (`GET /me`, `ownProfileSchema`),
   * fetched by the shell on every route; `users.detail` is *another*
   * member's public view (`GET /users/:id`, `publicProfileSchema`), fetched
   * only where a surface needs to render someone else. Two routes, two
   * schemas, so they keep two keys rather than one call site refetching a
   * wider shape than it asked for.
   *
   * ⚠ **Deliberately absent from `eventBlastRadius` below — not an
   * oversight.** `user.display_name_changed`, `user.bio_changed` and
   * `user.avatar_changed` are all `surfacesInFeed: false` and carry
   * `boardId: null` (`packages/shared/src/events.ts`), so per `CLAUDE.md`'s
   * own rule (`publishEvents` fans out on `board_id` alone) they never reach
   * the live socket at all — there is no frame this group could ever be
   * swept by. A member's rename reaching another member's screen is a
   * reload, exactly as `FR-PERM-002` AC2 says it is, not a live update.
   */
  users: {
    /** Every user-shaped entry. */
    all: () => ['users'] as const,
    /**
     * `GET /users/:id` — another member's public profile. Unused today;
     * first caller is `FE-44.2` (the Phase 2 public profile page), which
     * ships this spelling now so that block does not invent a second one —
     * see the file's own docstring above, "a block that needs a new entry
     * adds its builder here".
     */
    detail: (userId: string) => ['users', userId] as const,
    /** `GET /users/:id/profile-page` — B44 public profile-page aggregate. */
    profilePage: (userId: string) => ['users', userId, 'profile-page'] as const,
  },

  /**
   * SA-9 — the platform admin console (`GET /admin/*`, SA-8).
   *
   * Its own `['admin', …]` prefix rather than a nesting under `users`,
   * `workspaces` or `boards`: these routes serialise admin-only copies
   * (`adminUserSummarySchema` carries an email and lifecycle stamps
   * `publicProfileSchema` must never hold), so a product-wide `users` sweep must
   * not reach them and an admin sweep must not refetch a member's own surfaces.
   *
   * The lists vary by querystring on the `meInboxCards` shape; `user(id)` sits
   * beside them under the same `['admin', 'users']` prefix, so one invalidation
   * after an account action refreshes the row and the detail together. A
   * querystring (`q=…`) can never equal a ULID, so the third segment does not
   * collide.
   *
   * ⚠ Only the FIRST page is cached under a list key. "Load more" appends the
   * next page into that same entry (`lib/admin.ts`), so there is one entry per
   * filter and never one per cursor.
   */
  admin: {
    /** Every admin console entry. */
    all: () => ['admin'] as const,
    /** `GET /admin/users?q=` */
    users: (queryKey = '') =>
      queryKey === ''
        ? (['admin', 'users'] as const)
        : (['admin', 'users', queryKey] as const),
    /** `GET /admin/users/:id` */
    user: (userId: string) => ['admin', 'users', userId] as const,
    /**
     * `GET /admin/users/:userId/api-tokens` (B85, AT-5). Under `user(userId)`, so
     * an account action's `users()` invalidation refreshes it too.
     */
    apiTokens: (userId: string) => ['admin', 'users', userId, 'api-tokens'] as const,
    /** `GET /admin/workspaces?q=` */
    workspaces: (queryKey = '') =>
      queryKey === ''
        ? (['admin', 'workspaces'] as const)
        : (['admin', 'workspaces', queryKey] as const),
    /** `GET /admin/boards?q=&visibility=&closed=` */
    boards: (queryKey = '') =>
      queryKey === ''
        ? (['admin', 'boards'] as const)
        : (['admin', 'boards', queryKey] as const),
    /** `GET /admin/audit?actor=&target=&action=` */
    audit: (queryKey = '') =>
      queryKey === ''
        ? (['admin', 'audit'] as const)
        : (['admin', 'audit', queryKey] as const),
  },
} as const;

/**
 * The blast radius of one live frame, as query-key **prefixes**.
 *
 * First intended caller: `FE-04.16`'s socket client, for the frames it cannot
 * apply as a targeted `setQueryData` — an event type it does not model, or one
 * whose payload it cannot trust to be a complete row. Passing those to
 * `invalidateQueries` refetches rather than guesses.
 *
 * Takes the two nullable id fields of a `LiveFrame` structurally rather than
 * importing the type, so this module keeps its one job (spelling keys) and
 * `@molt/shared`'s event vocabulary stays the socket client's business.
 *
 * ⚠ These are **prefixes**, so they must be used with the default partial match
 * — `invalidateQueries({ queryKey })`, never `{ exact: true }`, which would
 * match `['boards', id]` alone and miss `['boards', id, 'contents']`, the one
 * entry the board face actually reads.
 */
export function eventBlastRadius(frame: {
  readonly boardId: string | null;
  readonly cardId: string | null;
}): MoltQueryKey[] {
  const keys: MoltQueryKey[] = [];
  if (frame.boardId !== null) {
    keys.push(queryKeys.boards.detail(frame.boardId));
    keys.push(queryKeys.powerUps.board(frame.boardId));
    keys.push(queryKeys.powerUps.boardSurfaces(frame.boardId));
  }
  if (frame.cardId !== null) keys.push(queryKeys.cards.detail(frame.cardId));
  return keys;
}
